import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bike, Eye, Inbox, LogOut, MessageCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/site/Logo";
import { api, bikeImage, formatINR } from "@/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const sellerWa = (r) => {
  const digits = (r.phone || "").replace(/\D/g, "");
  const num = digits.length === 10 ? `91${digits}` : digits;
  const text = `Hi ${r.name}! Deal 2 Wheel yahan — aapki ${r.bike_model} bechne ki request mili hai. Valuation ke baare mein baat karein?`;
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
};

const AdminDashboard = () => {
  const [bikes, setBikes] = useState([]);
  const [sellRequests, setSellRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("inventory");
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then(() => Promise.all([api.get("/bikes"), api.get("/admin/sell-requests"), api.get("/admin/bookings")]))
      .then(([bikesRes, sellRes, bookingsRes]) => {
        setBikes(bikesRes.data);
        setSellRequests(sellRes.data);
        setBookings(bookingsRes.data);
      })
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleDelete = async () => {
    try {
      await api.delete(`/bikes/${deleteId}`);
      setBikes(bikes.filter((b) => b.id !== deleteId));
      toast.success("Listing deleted");
    } catch {
      toast.error("Delete failed. Try again.");
    } finally {
      setDeleteId(null);
    }
  };

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("d2w_token");
    navigate("/admin/login");
  };

  const available = bikes.filter((b) => b.status !== "sold").length;
  const paidBookings = bookings.filter((b) => b.status === "paid").length;

  if (loading) {
    return (
      <div className="bg-[#050505] min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <LogoMark className="w-12 h-12 text-white animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <span className="flex items-center gap-2.5">
            <LogoMark className="w-8 h-8 text-white" />
            <span className="font-display font-semibold uppercase tracking-tight">Control Room</span>
          </span>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="text-xs uppercase tracking-widest text-white/50 hover:text-volt transition-colors duration-300 hidden sm:block">
              View Site
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-white/20 px-4 py-2 hover:border-volt hover:text-volt transition-colors duration-300"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="grid grid-cols-3 gap-3 mb-10">
          {[
            { icon: Bike, label: "Total Listings", value: bikes.length, testId: "stat-total" },
            { icon: Eye, label: "Available", value: available, testId: "stat-available" },
            { icon: Inbox, label: "Sell Requests", value: sellRequests.length, testId: "stat-sell-requests" },
          ].map((s) => (
            <div key={s.label} className="border border-white/10 bg-card p-5" data-testid={s.testId}>
              <s.icon className="w-5 h-5 text-volt mb-3" />
              <p className="font-display font-semibold text-3xl">{s.value}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setTab("inventory")}
              data-testid="tab-inventory"
              className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest border transition-colors duration-300 ${
                tab === "inventory" ? "bg-volt text-black border-volt" : "border-white/20 text-white/60 hover:text-volt"
              }`}
            >
              Inventory
            </button>
            <button
              onClick={() => setTab("requests")}
              data-testid="tab-requests"
              className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest border transition-colors duration-300 ${
                tab === "requests" ? "bg-volt text-black border-volt" : "border-white/20 text-white/60 hover:text-volt"
              }`}
            >
              Sell Requests ({sellRequests.length})
            </button>
            <button
              onClick={() => setTab("bookings")}
              data-testid="tab-bookings"
              className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest border transition-colors duration-300 ${
                tab === "bookings" ? "bg-volt text-black border-volt" : "border-white/20 text-white/60 hover:text-volt"
              }`}
            >
              Bookings ({paidBookings})
            </button>
          </div>
          {tab === "inventory" && (
            <Link
              to="/admin/bikes/new"
              data-testid="add-bike-button"
              className="inline-flex items-center gap-2 bg-volt text-black text-xs font-semibold uppercase tracking-widest px-5 py-2.5 hover:brightness-110 transition-[filter] duration-300"
            >
              <Plus className="w-4 h-4" /> Add Bike
            </Link>
          )}
        </div>

        {tab === "inventory" ? (
          <div className="border border-white/10 overflow-x-auto" data-testid="inventory-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-white/10 bg-card text-left">
                  {["Bike", "Price", "Year / KM", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bikes.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-14 text-center text-white/40" data-testid="inventory-table-empty">Koi listing nahi hai. "Add Bike" se shuru karo.</td></tr>
                )}
                {bikes.map((b) => (
                  <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`inventory-row-${b.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-4">
                        <img src={bikeImage(b.image)} alt={b.title} className="w-16 h-12 object-cover border border-white/10" />
                        <div>
                          <p className="font-semibold">{b.title}</p>
                          <p className="text-xs text-white/40">{b.brand} · {b.category}{b.featured ? " · Featured" : ""}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 font-display font-semibold text-volt">{formatINR(b.price)}</td>
                    <td className="px-5 py-4 text-white/60">{b.year} · {Number(b.km).toLocaleString("en-IN")} km</td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 ${b.status === "sold" ? "bg-destructive/20 text-red-400" : "bg-volt/15 text-volt"}`}>
                        {b.status === "sold" ? "Sold" : "Available"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2">
                        <Link
                          to={`/admin/bikes/${b.id}/edit`}
                          data-testid={`edit-bike-${b.id}`}
                          className="p-2.5 border border-white/15 hover:border-volt hover:text-volt transition-colors duration-300"
                          aria-label={`Edit ${b.title}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeleteId(b.id)}
                          data-testid={`delete-bike-${b.id}`}
                          className="p-2.5 border border-white/15 hover:border-red-500 hover:text-red-500 transition-colors duration-300"
                          aria-label={`Delete ${b.title}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "requests" ? (
          <div className="space-y-3" data-testid="sell-requests-list">
            {sellRequests.length === 0 && (
              <div className="border border-white/10 bg-card p-14 text-center text-white/40" data-testid="sell-requests-empty">
                Abhi koi sell request nahi aayi.
              </div>
            )}
            {sellRequests.map((r) => (
              <div key={r.id} className="border border-white/10 bg-card p-6 grid grid-cols-1 md:grid-cols-5 gap-4 items-center" data-testid={`sell-request-${r.id}`}>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Seller</p>
                  <p className="font-semibold">{r.name}</p>
                  <p className="text-sm text-volt">{r.phone}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Vehicle</p>
                  <p className="font-semibold">{r.bike_model}</p>
                  <p className="text-sm text-white/50">{r.year ? `${r.year} · ` : ""}{r.km ? `${Number(r.km).toLocaleString("en-IN")} km` : "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Expected</p>
                  <p className="font-display font-semibold text-volt">{r.expected_price ? formatINR(r.expected_price) : "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Note</p>
                  <p className="text-sm text-white/60">{r.message || "—"}</p>
                </div>
                <div className="md:text-right">
                  <a
                    href={sellerWa(r)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`sell-request-reply-${r.id}`}
                    className="inline-flex items-center gap-2 bg-volt text-black text-xs font-semibold uppercase tracking-widest px-5 py-3 hover:brightness-110 transition-[filter] duration-300"
                  >
                    <MessageCircle className="w-4 h-4" /> Reply
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="bookings-list">
            {bookings.length === 0 && (
              <div className="border border-white/10 bg-card p-14 text-center text-white/40" data-testid="bookings-empty">
                Abhi koi booking nahi aayi.
              </div>
            )}
            {bookings.map((b) => (
              <div key={b.id} className="border border-white/10 bg-card p-6 grid grid-cols-1 md:grid-cols-5 gap-4 items-center" data-testid={`booking-${b.id}`}>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Customer</p>
                  <p className="font-semibold">{b.name}</p>
                  <p className="text-sm text-volt">{b.phone}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Bike</p>
                  <p className="font-semibold">{b.bike_title}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Amount</p>
                  <p className="font-display font-semibold text-volt">{formatINR((b.amount_paise || 0) / 100)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Status</p>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 ${
                    b.status === "paid" ? "bg-volt/15 text-volt" : b.status === "failed" ? "bg-destructive/20 text-red-400" : "bg-white/10 text-white/50"
                  }`} data-testid={`booking-status-${b.id}`}>
                    {b.status === "paid" ? "Paid" : b.status === "failed" ? "Failed" : "Pending"}
                  </span>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Date</p>
                  <p className="text-sm text-white/60">{new Date(b.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-white/10 text-white rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Listing delete karein?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              Ye listing permanently hata di jayegi. Ye action undo nahi ho sakta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="bg-transparent border-white/20 text-white hover:bg-white/10 rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={handleDelete} className="bg-destructive text-white hover:bg-destructive/90 rounded-none">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
